import { Gobbler } from '@groww-tech-pvt/gobbler';
import {
  EventRequestV2,
  UserContext,
} from '@groww-tech-pvt/gobbler-proto-ts/dist/EventRequestV2';
import {
  getDeviceModelInfo,
  getNetworkType,
  getOSName,
  getOSVersion,
} from '@groww-tech-pvt/ella';

import { WEBSITE_VERSION } from 'src/utils/constants/websiteVersion';
import { ENVS, resolveEnvFlavour } from 'src/utils/config/domainConfig';


export const gb = new Gobbler({
  apikey: ENV_GOBBLER_API_KEY,
  baseUrl: ENV_GOBBLER_API_URL,
  eventRequestV2: EventRequestV2,
});

export function getDeviceContext() {
  return gb.getDeviceContext();
}

// Helper function to get basic device context data
function getBasicDeviceContextData() {
  const deviceModelInfo = getDeviceModelInfo();

  return {
    appVersionCode: 0,
    appVersionName: WEBSITE_VERSION,
    carrier: '', // Not available in browser context
    manufacturer: deviceModelInfo.manufacturer,
    model: deviceModelInfo.model,
    networkType: getNetworkType(),
    osName: getOSName() ?? '',
    osVersion: getOSVersion(),
    sdkVersion: '',
  };
}

// Initialize device context with basic data (deviceId will be empty initially)
function initializeDeviceContext() {
  const basicContext = getBasicDeviceContextData();

  gb.setDeviceContext({
    ...basicContext,
    deviceId: '', // Empty initially, can be updated later
  });
}

// Set device context automatically on module initialization
initializeDeviceContext();

export function setDeviceContext(deviceId: string) {
  const basicContext = getBasicDeviceContextData();

  gb.setDeviceContext({
    ...basicContext,
    deviceId: deviceId ?? '',
  });
}

export function setUserContext(userContext: UserContext) {
  gb.setUserContext(userContext);
}

type GobblerEvent = {
  encode: (message: any) => {
    finish: () => Uint8Array;
  };
};

/**
 * track
 *
 * @remarks
 * This function is used to send events to gobbler and optionally to Google Analytics.
 *
 * @param name - The name of the event in string
 * @param event - The event that has to be sent to gobbler
 * @param payload - The attributes that is to be sent with the event
 * @param sendToGA - Whether to send the event to Google Analytics (default: false)
 * ```
 */
export const trackEvent = async <T extends GobblerEvent>(
  eventName: string,
  event: T,
  payload: Parameters<T['encode']>[0],
  sendToGA: boolean = true
) => {
  const name = eventName;
  const flavour = resolveEnvFlavour();

  if (flavour.includes(ENVS.NEXT) || flavour.includes(ENVS.UAT)) {
    console.log({ eventName, payload });
  }
  // Gobbler / GA are wired for production traffic only (use runtime host, not build-time FLAVOUR).
  if (flavour === ENVS.PROD) {
    try {
      const encodedByteArray = event.encode(payload).finish();

      const GOBBLER_API_MAX_RETRIES = 2;
      let retryAttempts = 0;
      let isSuccess = false;

      while (retryAttempts <= GOBBLER_API_MAX_RETRIES && !isSuccess) {
        try {
          await gb.track(name, encodedByteArray);
          isSuccess = true;
        } catch (e) {
          retryAttempts++;

          // Only capture the failure if we've exhausted all retries
          if (retryAttempts > GOBBLER_API_MAX_RETRIES) {
            console.error('Failed to send event to gobbler', e);
          }

          // Exponential backoff for retries
          if (!isSuccess && retryAttempts <= GOBBLER_API_MAX_RETRIES) {
            // captureGobblerBackoffTriggered(name, payload, retryAttempts);
            await new Promise(resolve =>
              setTimeout(resolve, 1000 * retryAttempts)
            );
          }
        }
      }

      if (sendToGA) {
        sendGAEvent(name, payload);
      }
    } catch (e) {
      //error for encoding failed
      console.error(e);
    }
  }
};

// Type definitions for gtag
declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'js',
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
  }
}

type GAValue = string | number | boolean;

const MAX_PARAM_LENGTH = 100; // GA4 safe-ish
const MAX_STRING_LENGTH = 100; // per param (GA limit is ~100 chars in UI)


function truncate(str: string, max = MAX_STRING_LENGTH) {
  return str.length > max ? str.slice(0, max) : str;
}

function flatten(
  value: unknown,
  prefix: string,
  output: Record<string, GAValue>
) {
  if (value == null) return;

  // Primitive values
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    output[prefix] = typeof value === 'string' ? truncate(value) : value;
    return;
  }

  // Array → stringify safely
  if (Array.isArray(value)) {
    output[`${prefix}_count`] = value.length;
    output[prefix] = truncate(value.join('|'));
    return;
  }

  // Object → recurse
  if (typeof value === 'object') {
    Object.entries(value).forEach(([key, val]) => {
      const nextKey = prefix ? `${prefix}_${key}` : key;
      flatten(val, nextKey, output);
    });
  }
}

export function normalizePayload(
  payload: Record<string, unknown>
): Record<string, GAValue> {
  const result: Record<string, GAValue> = {};

  flatten(payload, '', result);

  // GA allows max ~25 params → keep important ones
  return Object.fromEntries(Object.entries(result).slice(0, MAX_PARAM_LENGTH));
}


/**
 * Send a custom event to Google Analytics
 */
export const sendGAEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  const env = resolveEnvFlavour();
  if (env.includes(ENVS.NEXT) || env.includes(ENVS.UAT)) {
    console.log({ eventName, eventParams });
  }

  if (env === ENVS.PROD) {
    if (typeof window !== 'undefined' && window.gtag) {
      const params = normalizePayload(eventParams ?? {});
      window.gtag('event', eventName, params);
    }
  }
};
